import { mat4, ReadonlyMat4, quat, ReadonlyVec3, vec3 } from 'gl-matrix';
import { concatSlices, unflattenToSlices } from '~/domain/sdfCompression';
import { resampleUint8Grid } from '~/domain/services/resampleGrid';
import { Grid } from '~/engine/data/common/Grid';
import { convexHull } from '~/engine/data/common/tesselateConvexPolygon';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import {
    IMAGE_RANGE_TOLERANCE_VOXELS,
    SDF_ISO_THRESHOLD,
} from '~/engine/util/algorithms/sdf/sdfConstants';
import {
    assertGlWorkLanded,
    discardPendingGlErrors,
} from '~/engine/util/gl/GLUtilities';
import { errorName } from '~/utils/errors';
import { ToolAction } from '../../ToolAction';

/**
 * Snap a world-space position onto the image grid's voxel lattice.
 *
 * Projects the position into the image grid's voxel coordinate frame,
 * rounds each axis to the nearest integer voxel boundary, and converts
 * back to world space. This guarantees that any Grid constructed with
 * the snapped position shares the same voxel phase as the image grid,
 * which prevents sub-voxel misalignment in downstream operations
 * (boolean ops, margin expansion, dose mapping).
 *
 * @param point      Position in patient (world) coordinates
 * @param imageGrid  Reference image grid that defines the canonical lattice
 * @returns          Snapped position in patient (world) coordinates
 */
export function snapPointToImageGrid(
    point: ReadonlyVec3,
    imageGrid: Grid
): vec3 {
    const voxel = imageGrid.toVoxelSpace(point);

    // Snap each axis to nearest voxel boundary
    voxel[0] = Math.round(voxel[0]);
    voxel[1] = Math.round(voxel[1]);
    voxel[2] = Math.round(voxel[2]);

    return imageGrid.toWorldSpace(voxel);
}

/**
 * `grid`'s transformation with its origin at `origin`: copied, as one rebuilt
 * from a float32 quaternion drifts from the image's with every commit.
 */
function transformationAt(grid: Grid, origin: ReadonlyVec3): mat4 {
    const transformation = mat4.clone(grid.getTransformation());
    [transformation[12], transformation[13], transformation[14]] = origin;
    return transformation;
}

/**
 * Build a copy of a grid whose origin has been snapped to the image
 * grid's voxel lattice. Dimensions and resolution are preserved;
 * only the origin changes.
 *
 * Used on mouse release in transform actions so the committed
 * structure stays aligned with the canonical image grid.
 */
export function buildSnappedGrid(grid: Grid, imageGrid: Grid): Grid {
    const snappedOrigin = snapPointToImageGrid(grid.getPosition(), imageGrid);
    const snappedTransform = transformationAt(grid, snappedOrigin);
    return new Grid(
        grid.sizeX,
        grid.sizeY,
        grid.sizeZ,
        grid.resX,
        grid.resY,
        grid.resZ,
        snappedTransform
    );
}

/**
 * Patient-space scale by `scale` around `pivot`, along the unit `axis` only
 * when one is given. The scale `1 / s` undoes the scale `s`.
 */
export function buildScaleAroundPivot(
    pivot: ReadonlyVec3,
    scale: number,
    axis?: ReadonlyVec3
): mat4 {
    const m = mat4.create();
    mat4.translate(m, m, pivot as vec3);
    if (axis) {
        // I + (scale - 1) * axis * axis^T
        const alongAxis = mat4.create();
        for (let column = 0; column < 3; column++) {
            for (let row = 0; row < 3; row++) {
                alongAxis[column * 4 + row] +=
                    (scale - 1) * axis[row] * axis[column];
            }
        }
        mat4.multiply(m, m, alongAxis);
    } else {
        mat4.scale(m, m, [scale, scale, scale]);
    }
    mat4.translate(m, m, vec3.negate(vec3.create(), pivot as vec3));
    return m;
}

/**
 * Build a forward rotation matrix around a pivot point.
 *
 * Computes: T(pivot) * R(axis, angle) * T(-pivot)
 *
 * Used during drag to rotate the grid for visual preview.
 */
export function buildRotationAroundPivot(
    pivot: ReadonlyVec3,
    axis: ReadonlyVec3,
    angle: number
): mat4 {
    const q = quat.create();
    quat.setAxisAngle(q, axis as vec3, angle);
    const rotMat = mat4.fromQuat(mat4.create(), q);

    const m = mat4.create();
    mat4.translate(m, m, pivot as vec3);
    mat4.multiply(m, m, rotMat);
    mat4.translate(m, m, vec3.negate(vec3.create(), pivot as vec3));
    return m;
}

/**
 * Build an axis-aligned destination grid that encompasses the
 * rotated structure, using the image grid's orientation and
 * resolution.
 *
 * After rotation the structure grid has a different orientation
 * than the image grid. This function:
 * 1. Transforms all 8 corners of the original grid by the
 *    rotation matrix.
 * 2. Projects them into the image grid's voxel space to find
 *    the axis-aligned bounding box.
 * 3. Constructs a new Grid with the image grid's orientation
 *    and resolution, sized to cover the AABB.
 * 4. Snaps the origin to the image lattice.
 *
 * @param originalGrid  The structure grid before rotation
 * @param rotationMat   Forward rotation matrix (pivot-centred)
 * @param imageGrid     Reference image grid for orientation and
 *                      resolution
 */
export function buildAxisAlignedDestGrid(
    originalGrid: Grid,
    rotationMat: ReadonlyMat4,
    imageGrid: Grid
): Grid {
    const res = imageGrid.getRes();

    // Compute the 8 corners of the original grid in world space,
    // then rotate them
    const srcSize = originalGrid.getSize();
    const voxelToWorld = originalGrid.voxelToWorld as mat4;

    let minVoxel = vec3.fromValues(Infinity, Infinity, Infinity);
    let maxVoxel = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    // Iterate over the 8 corners of the voxel grid
    for (let iz = 0; iz <= 1; iz++) {
        for (let iy = 0; iy <= 1; iy++) {
            for (let ix = 0; ix <= 1; ix++) {
                const voxelCorner = vec3.fromValues(
                    ix * srcSize[0],
                    iy * srcSize[1],
                    iz * srcSize[2]
                );

                // Voxel → world (original) → world (rotated)
                const worldPt = vec3.transformMat4(
                    vec3.create(),
                    voxelCorner,
                    voxelToWorld
                );
                const rotatedPt = vec3.transformMat4(
                    vec3.create(),
                    worldPt,
                    rotationMat as mat4
                );

                // Project into image voxel space
                const imageVoxel = imageGrid.toVoxelSpace(rotatedPt);
                vec3.min(minVoxel, minVoxel, imageVoxel);
                vec3.max(maxVoxel, maxVoxel, imageVoxel);
            }
        }
    }

    // Add 1-voxel padding to avoid boundary clipping
    minVoxel = vec3.fromValues(
        Math.floor(minVoxel[0]) - 1,
        Math.floor(minVoxel[1]) - 1,
        Math.floor(minVoxel[2]) - 1
    );
    maxVoxel = vec3.fromValues(
        Math.ceil(maxVoxel[0]) + 1,
        Math.ceil(maxVoxel[1]) + 1,
        Math.ceil(maxVoxel[2]) + 1
    );

    const newSizeX = Math.max(1, maxVoxel[0] - minVoxel[0]);
    const newSizeY = Math.max(1, maxVoxel[1] - minVoxel[1]);
    const newSizeZ = Math.max(1, maxVoxel[2] - minVoxel[2]);

    // Convert the min corner back to world space for the origin
    const newOrigin = imageGrid.toWorldSpace(minVoxel);

    const newTransform = transformationAt(imageGrid, newOrigin);

    return new Grid(
        Math.round(newSizeX),
        Math.round(newSizeY),
        Math.round(newSizeZ),
        res[0],
        res[1],
        res[2],
        newTransform
    );
}

type Interval = [number, number];
type PlanePoint = [number, number];
/** An in-plane move of (x, y) mm makes `a * x + b * y` image voxels on one axis, within [low, high]. */
type PlaneLimit = [a: number, b: number, low: number, high: number];

/** How far, in image voxels, a solved position may miss its limits. */
const FIT_TOLERANCE = 1e-7;
const BISECTION_STEPS = 12;
/** How far above 1 a transform's determinant may come and still not enlarge what it moves. */
const ENLARGING_DETERMINANT = 1 + 1e-5;
/** How far, in image voxels, float32 rounding can move a point a transform leaves in place. */
const ROUNDING_VOXELS = 1e-4;

/**
 * Patient-space points whose convex hull is that of the content voxel centres
 * of `flatPixels`, `grid`'s, z-major: each row's first and last content voxel,
 * reduced per slice by a 2-D hull.
 */
export function extractContentHullPoints(
    flatPixels: Uint8Array,
    grid: Grid
): Float64Array {
    const [sizeX, sizeY, sizeZ] = grid.getSize();
    const isContent = (i: number) => flatPixels[i] > SDF_ISO_THRESHOLD;
    const points: number[] = [];
    for (let z = 0; z < sizeZ; z++) {
        const rowEnds: [number, number][] = [];
        for (let y = 0; y < sizeY; y++) {
            const row = (z * sizeY + y) * sizeX;
            let [first, last] = [0, sizeX - 1];
            while (first <= last && !isContent(row + first)) {
                first++;
            }
            while (last > first && !isContent(row + last)) {
                last--;
            }
            if (first <= last) {
                rowEnds.push([first, y], [last, y]);
            }
        }
        for (const [x, y] of convexHull(rowEnds)) {
            points.push(...grid.voxelCenterToWorldSpace([x, y, z]));
        }
    }
    return Float64Array.from(points);
}

/**
 * What an image allows one structure across a gesture, in its voxel
 * coordinates. On each image axis, the extent of the structure's hull:
 * - if it fits the image's voxel-centre range, stays inside each face it lay
 *   inside at the press or has reached since (a held face), and goes no
 *   further out than at the press past any other;
 * - if it is wider than that range, may also move, short of growing, as long
 *   as it spans the range or comes no further from spanning it than at the
 *   press.
 */
export class ImageBounds {
    private readonly _range: Interval[];
    private readonly _press: Interval[];
    private readonly _held = Array<boolean>(6).fill(false);

    /** @param _hull  The structure's hull points, patient-space xyz triples, mm */
    public constructor(
        private readonly _hull: Float64Array,
        private readonly _imageGrid: Grid
    ) {
        const { min, max } = _imageGrid.voxelCenterRange();
        this._range = [0, 1, 2].map(
            (axis): Interval => [
                min[axis] - IMAGE_RANGE_TOLERANCE_VOXELS,
                max[axis] + IMAGE_RANGE_TOLERANCE_VOXELS,
            ]
        );
        this._press = this.extents();
        this.hold();
    }

    /** Per image axis, the least and greatest voxel coordinate of the hull, moved by `transform`. */
    public extents(transform?: ReadonlyMat4): Interval[] {
        const extents = [0, 1, 2].map((): Interval => [Infinity, -Infinity]);
        this._imageGrid
            .pointsToVoxelSpace(this._hull, transform)
            .forEach((v, k) => {
                const extent = extents[k % 3];
                extent[0] = Math.min(extent[0], v);
                extent[1] = Math.max(extent[1], v);
            });
        return extents;
    }

    /** Whether `transform`, patient space, stays within the bounds. */
    public keeps(transform: ReadonlyMat4): boolean {
        const grows = mat4.determinant(transform) > ENLARGING_DETERMINANT;
        return this.extents(transform).every(([low, high], axis) => {
            const [pressLow, pressHigh] = this._press[axis];
            const [min, max] = this._range[axis];
            const [heldLow, heldHigh] = this._held.slice(2 * axis);
            const [lowest, highest] = [
                heldLow ? min : Math.min(min, pressLow - ROUNDING_VOXELS),
                heldHigh ? max : Math.max(max, pressHigh + ROUNDING_VOXELS),
            ];
            const staysSpanning =
                !grows &&
                !heldLow &&
                !heldHigh &&
                pressHigh - pressLow > max - min &&
                low <= Math.max(min, pressLow + ROUNDING_VOXELS) &&
                high >= Math.min(max, pressHigh - ROUNDING_VOXELS);
            return (low >= lowest && high <= highest) || staysSpanning;
        });
    }

    /**
     * Per image axis, the limit on an in-plane move along the unit `planeAxes`:
     * the voxel offsets a rigid shift of the structure may make.
     */
    public planeLimits(
        planeAxes: readonly [ReadonlyVec3, ReadonlyVec3]
    ): PlaneLimit[] {
        const [stepX, stepY] = planeAxes.map((direction) => {
            const step = this._imageGrid.rotateToGrid(direction);
            return vec3.div(step, step, this._imageGrid.getRes());
        });
        return this._range.map(([min, max], axis): PlaneLimit => {
            const [pressLow, pressHigh] = this._press[axis];
            const [toLow, toHigh] = [min - pressLow, max - pressHigh];
            const [a, b] = [stepX[axis], stepY[axis]];
            return toLow > toHigh
                ? [a, b, Math.min(toHigh, 0), Math.max(toLow, 0)]
                : [
                      a,
                      b,
                      this._held[2 * axis] ? toLow : Math.min(toLow, 0),
                      this._held[2 * axis + 1] ? toHigh : Math.max(toHigh, 0),
                  ];
        });
    }

    /** Holds each face that `transform` leaves inside it a structure fitting the image. */
    public hold(transform?: ReadonlyMat4): void {
        this.extents(transform).forEach(([low, high], axis) => {
            const [min, max] = this._range[axis];
            if (high - low <= max - min) {
                this._held[2 * axis] ||= low >= min;
                this._held[2 * axis + 1] ||= high <= max;
            }
        });
    }

    /** Zeroes the voxels of `slices`, on `grid` of any sampling, beyond a held face. */
    public clearBeyondHeld(slices: Uint8Array[], grid: Grid): void {
        const kept = this._range.map(([min, max], axis) =>
            grid.columnsWithin(
                this._imageGrid,
                axis,
                this._held[2 * axis] ? min : -Infinity,
                this._held[2 * axis + 1] ? max : Infinity
            )
        );
        const [sizeX, sizeY] = grid.getSize();
        slices.forEach((slice, z) => {
            for (let y = 0; y < sizeY; y++) {
                const row = y * sizeX;
                for (const columns of kept) {
                    const [first, last] = columns(y, z);
                    slice.fill(0, row, row + Math.min(first, sizeX));
                    slice.fill(0, row + Math.max(last + 1, 0), row + sizeX);
                }
            }
        });
    }
}

/**
 * `to` if its transform stays within `bounds`, else the last value from `from`
 * towards `to` whose transform does, found by bisection; `bounds` then hold
 * the faces it reaches.
 */
export function clampTransformToContent(
    from: number,
    to: number,
    transformAt: (value: number) => ReadonlyMat4,
    bounds: ImageBounds
): number {
    let [inside, outside] = bounds.keeps(transformAt(to))
        ? [to, to]
        : [from, to];
    for (let step = 0; inside !== outside && step < BISECTION_STEPS; step++) {
        const middle = (inside + outside) / 2;
        [inside, outside] = bounds.keeps(transformAt(middle))
            ? [middle, outside]
            : [inside, middle];
    }
    bounds.hold(transformAt(inside));
    return inside;
}

/**
 * The point nearest to `requested` (mm along two orthonormal plane axes) that
 * meets every limit: the point itself, its projection on an edge line, or a
 * corner of that polygon; undefined when none of those does.
 */
function nearestInPlane(
    requested: PlanePoint,
    limits: readonly PlaneLimit[]
): PlanePoint | undefined {
    // An axis the plane cannot move the content along adds no edge and no
    // limit: a move changes it by rounding noise alone.
    const moving = limits.filter(([a, b]) => Math.hypot(a, b) >= 1e-6);
    const lines = moving.flatMap(([a, b, low, high]) =>
        [low, high]
            .filter(Number.isFinite)
            .map((offset) => [a, b, offset] as const)
    );
    const [rx, ry] = requested;
    const candidates: PlanePoint[] = [requested];
    for (const [i, [a, b, offset]] of lines.entries()) {
        const t = (offset - a * rx - b * ry) / (a * a + b * b);
        candidates.push([rx + t * a, ry + t * b]);
        for (const [c, d, other] of lines.slice(i + 1)) {
            const det = a * d - b * c;
            if (Math.abs(det) > 1e-12) {
                const x = (offset * d - b * other) / det;
                candidates.push([x, (a * other - c * offset) / det]);
            }
        }
    }
    const fits = ([x, y]: PlanePoint) =>
        moving.every(([a, b, low, high]) => {
            const v = a * x + b * y;
            return v >= low - FIT_TOLERANCE && v <= high + FIT_TOLERANCE;
        });
    const distance = ([x, y]: PlanePoint) => Math.hypot(x - rx, y - ry);
    return candidates
        .filter(fits)
        .toSorted((p, q) => distance(p) - distance(q))[0];
}

/**
 * The patient-space translation (mm) nearest to `delta` in the plane of the
 * unit `planeAxes` that stays within every one of `bounds`, which then hold the
 * faces it reaches.
 */
export function clampTranslationToContent(
    delta: ReadonlyVec3,
    bounds: readonly ImageBounds[],
    planeAxes: readonly [ReadonlyVec3, ReadonlyVec3]
): vec3 {
    const [axisX, axisY] = planeAxes;
    const [x, y] = nearestInPlane(
        [vec3.dot(delta, axisX), vec3.dot(delta, axisY)],
        bounds.flatMap((b) => b.planeLimits(planeAxes))
    ) ?? [0, 0];
    const allowed = vec3.scale(vec3.create(), axisX, x);
    vec3.scaleAndAdd(allowed, allowed, axisY, y);
    const translation = mat4.fromTranslation(mat4.create(), allowed);
    bounds.forEach((b) => b.hold(translation));
    return allowed;
}

/** One structure's part of {@link TransformedStructure.commitAll}. */
export interface StructureCommit {
    readonly structure: TransformedStructure;
    readonly srcGrid: Grid;
    readonly dstGrid: Grid;
    readonly patientSrcFromDst?: ReadonlyMat4;
}

/**
 * A structure across one transform gesture: its content at the press, its undo
 * step, and the bounds its image sets its whole content.
 */
export class TransformedStructure {
    /** The content's texture grid at the press, unmoved by the gesture. */
    public readonly grid: Grid;
    private readonly _flatPixels: Uint8Array;
    private _write?: TextureWrite;
    private _bounds?: ImageBounds;
    private _pixelsReplaced = false;

    private constructor(
        public readonly roi: RegionOfInterest,
        public readonly texture: ContourTexture
    ) {
        this.grid = texture.textureGrid.clone();
        this._flatPixels = concatSlices(texture.pixels);
    }

    /** The structure's content as pressed, or undefined when it has none. */
    public static of(roi: RegionOfInterest): TransformedStructure | undefined {
        const texture = roi.texture;
        return texture && !texture.isEmpty
            ? new TransformedStructure(roi, texture)
            : undefined;
    }

    /**
     * Commits every started structure, or none: on a failure all of them go
     * back as pressed with no undo step and the failure is rethrown, and a
     * gesture that moved none of them writes nothing. Once all of them hold
     * their new content, every undo step closes, even after one fails to.
     */
    public static commitAll(commits: readonly StructureCommit[]): void {
        const started = commits.filter(({ structure }) => structure._write);
        const structures = started.map(({ structure }) => structure);
        const moves = (commit: StructureCommit) =>
            !commit.srcGrid.hasEqualGeometryTo(commit.structure.grid) ||
            !mat4.equals(
                commit.patientSrcFromDst ?? mat4.create(),
                mat4.create()
            );
        if (!started.some(moves)) {
            TransformedStructure.cancelAll(structures);
            return;
        }
        try {
            const resampled = started.map((commit) =>
                commit.structure.resampled(commit)
            );
            discardPendingGlErrors();
            started.forEach(({ structure, dstGrid }, i) => {
                structure._pixelsReplaced = true;
                // The texture keeps the slices as its pixels, which the undo
                // step then reads without a GPU readback.
                structure.texture.resetSdfAndGrid(resampled[i], dstGrid, {
                    adoptPixels: true,
                });
            });
            // WebGL reports a failed allocation or upload only this way.
            assertGlWorkLanded('TransformedStructure: commit');
        } catch (error) {
            TransformedStructure.cancelAll(structures);
            throw error;
        }
        let failure: { error: unknown } | undefined;
        for (const structure of structures) {
            try {
                structure._write?.finish();
            } catch (error) {
                failure ??= { error };
            }
            structure._write = undefined;
        }
        if (failure) {
            throw failure.error;
        }
    }

    /**
     * Puts each structure back as pressed and drops its undo step. One that
     * cannot be put back is logged, and the others still go back.
     */
    public static cancelAll(structures: readonly TransformedStructure[]): void {
        for (const structure of structures) {
            try {
                structure.cancel();
            } catch (error) {
                console.error(
                    `TransformedStructure: could not put a structure back (${errorName(error)})`
                );
            }
        }
    }

    /** On the first call opens the undo step and sets the bounds, which it returns. */
    public start(operation: string): ImageBounds {
        this._write ??= this.roi.startModify(operation);
        this._bounds ??= new ImageBounds(
            extractContentHullPoints(this._flatPixels, this.grid),
            this.texture.grid
        );
        return this._bounds;
    }

    /**
     * The corners, patient mm, of the box the content fills as the gesture has
     * moved it, out to the faces of its outermost voxels on the image's
     * lattice; undefined outside a started gesture.
     */
    public footprint(): vec3[] | undefined {
        if (!this._write || !this._bounds) {
            return undefined;
        }
        const moved = mat4.multiply(
            mat4.create(),
            this.texture.textureGrid.voxelToWorld,
            this.grid.worldToVoxel
        );
        const faces = this._bounds
            .extents(moved)
            .map(([low, high]) => [low - 0.5, high + 0.5]);
        return [0, 1, 2, 3, 4, 5, 6, 7].map((corner) =>
            this.texture.grid.toWorldSpace([
                faces[0][corner & 1],
                faces[1][(corner >> 1) & 1],
                faces[2][corner >> 2],
            ])
        );
    }

    /** The content resampled for `commit`, slivers beyond a held face cleared. */
    private resampled({
        srcGrid,
        dstGrid,
        patientSrcFromDst,
    }: StructureCommit): Uint8Array[] {
        const { pixels } = resampleUint8Grid(
            this._flatPixels,
            srcGrid,
            dstGrid,
            { patientSrcFromDst, backgroundValue: 0 }
        );
        const { sizeX, sizeY, sizeZ } = dstGrid;
        const slices = unflattenToSlices(pixels, sizeX, sizeY, sizeZ);
        this._bounds?.clearBeyondHeld(slices, dstGrid);
        return slices;
    }

    private cancel(): void {
        const write = this._write;
        this._write = undefined;
        if (!write) {
            // A startModify that threw opened the modification and left no
            // write to cancel it.
            this.roi.resetModificationFlag();
            return;
        }
        try {
            if (this._pixelsReplaced) {
                const { sizeX, sizeY, sizeZ } = this.grid;
                this.texture.resetSdfAndGrid(
                    unflattenToSlices(this._flatPixels, sizeX, sizeY, sizeZ),
                    this.grid
                );
            } else {
                this.texture.resetGrid(this.grid);
            }
        } finally {
            write.cancel();
        }
    }
}

/** A gesture that transforms structures, which go back as pressed when it is cancelled. */
export abstract class StructureTransformAction extends ToolAction {
    /** The structures the gesture transforms, the one pressed first. */
    protected structures: TransformedStructure[] = [];

    /** Ends the gesture with every structure back as pressed. */
    public cancel(): void {
        TransformedStructure.cancelAll(this.structures.splice(0));
    }

    /** {@link TransformedStructure.footprint} of the structure pressed. */
    public footprint(): vec3[] | undefined {
        return this.structures[0]?.footprint();
    }

    /**
     * {@link TransformedStructure.commitAll} of every structure with the
     * grids `build` gives it. When `build` throws, the structures stay for
     * {@link cancel} to put back.
     */
    protected commitStructures(
        build: (s: TransformedStructure) => Omit<StructureCommit, 'structure'>
    ): void {
        const commits = this.structures.map((structure) => ({
            structure,
            ...build(structure),
        }));
        this.structures = [];
        TransformedStructure.commitAll(commits);
    }
}
